import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs';
import { ArrowLeft, Edit, FileText, Activity, BookOpen, Clock, Target, X, CheckCircle } from 'lucide-react';
import { useData } from '../../contexts/DataContext';

export default function ProfileDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { profiles, updateProfile } = useData();

    // Find the real profile from context; fall back to a default
    const foundProfile = profiles.find(p => p.id?.toString() === id?.toString());
    const [profileData, setProfileData] = useState(foundProfile || {
        id,
        name: 'Alex Johnson',
        age: 7,
        dob: '2019-05-14',
        diagnosis: 'Autism Spectrum Disorder',
        supportLevel: 'Level 2',
        status: 'Active',
        parents: 'Sarah & Mark Johnson',
        contact: '+1 (555) 123-4567',
        school: 'Oakbridge Elementary',
        grade: '2nd Grade',
    });

    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editForm, setEditForm] = useState({ ...profileData });
    const [saved, setSaved] = useState(false);

    const handleSaveEdit = (e) => {
        e.preventDefault();
        setProfileData({ ...editForm });
        if (updateProfile) updateProfile(editForm);
        setSaved(true);
        setTimeout(() => {
            setSaved(false);
            setEditModalOpen(false);
        }, 1500);
    };

    const handleNewAssessment = () => {
        navigate('/Screening_test/app/assessments/take', {
            state: { patient: profileData, assessment: { title: 'Therapist Clinical Evaluation' } }
        });
    };

    return (
        <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <Link to="/Screening_test/app/profiles">
                        <Button variant="ghost" size="icon" className="hover:bg-slate-200">
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                    </Link>
                    <div className="flex items-center gap-3">
                        <div className="h-14 w-14 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 text-xl font-bold">
                            {profileData.name?.charAt(0) || '?'}
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900">{profileData.name}</h1>
                            <p className="text-sm text-slate-500">ID: {profileData.id} • {profileData.age} years old</p>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <Badge variant="success" className="px-3 py-1">{profileData.status || 'Active'}</Badge>
                    <Button variant="outline" onClick={() => { setEditForm({ ...profileData }); setSaved(false); setEditModalOpen(true); }}>
                        <Edit className="mr-2 h-4 w-4" /> Edit Profile
                    </Button>
                    <Button onClick={handleNewAssessment}>
                        <FileText className="mr-2 h-4 w-4" /> New Assessment
                    </Button>
                </div>
            </div>

            {/* Tabs Layout */}
            <Tabs defaultValue="overview">
                <TabsList className="mb-6 bg-transparent p-0 gap-2 border-b border-slate-200 rounded-none w-full justify-start overflow-x-auto">
                    {['overview', 'educational', 'medical', 'history', 'iep'].map(tab => (
                        <TabsTrigger key={tab} value={tab}
                            className="border-b-2 border-transparent data-[state=active]:border-brand-600 rounded-none bg-transparent hover:bg-slate-50 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-brand-600 capitalize">
                            {tab === 'iep' ? 'IEP Goals' : tab === 'history' ? 'Assessments' : tab}
                        </TabsTrigger>
                    ))}
                </TabsList>

                <TabsContent value="overview">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <div className="lg:col-span-2 space-y-6">
                            <Card>
                                <CardHeader><CardTitle>Basic Information</CardTitle></CardHeader>
                                <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <InfoField label="Date of Birth" value={profileData.dob || '—'} />
                                    <InfoField label="Primary Diagnosis" value={profileData.diagnosis || '—'} />
                                    <InfoField label="Support Level" value={profileData.supportLevel || '—'} />
                                    <InfoField label="Primary Contacts" value={profileData.parents || '—'} />
                                    <InfoField label="Phone" value={profileData.contact || '—'} />
                                    <InfoField label="Current Grade" value={profileData.grade || '—'} />
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle>Recent Activity</CardTitle>
                                    <CardDescription>Latest assessments and goal updates</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-200 before:to-transparent">
                                        {[
                                            { icon: Activity, color: 'bg-brand-100 text-brand-600', title: 'IEP Goal Achieved', time: '2 days ago', desc: 'Mastered 2-step instructions with 80% accuracy.' },
                                            { icon: FileText, color: 'bg-secondary-100 text-secondary-600', title: 'Quarterly Assessment Completed', time: '1 week ago', desc: 'Overall score 68%. Improvements shown in social interaction.' },
                                        ].map((item, i) => (
                                            <div key={i} className="relative flex items-center gap-4">
                                                <div className={`flex items-center justify-center w-10 h-10 rounded-full border border-white ${item.color} shadow shrink-0 z-10`}>
                                                    <item.icon className="h-4 w-4" />
                                                </div>
                                                <div className="bg-white p-4 rounded border border-slate-200 shadow-sm flex-1">
                                                    <div className="flex items-center justify-between mb-1">
                                                        <span className="font-bold text-slate-900 text-sm">{item.title}</span>
                                                        <span className="text-xs text-slate-500">{item.time}</span>
                                                    </div>
                                                    <div className="text-sm text-slate-600">{item.desc}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        <div className="space-y-6">
                            <Card>
                                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Target className="h-5 w-5 text-accent-500" /> Focus Areas</CardTitle></CardHeader>
                                <CardContent>
                                    <div className="flex flex-wrap gap-2">
                                        {['Expressive Language', 'Fine Motor', 'Peer Interaction', 'Following Routines'].map(a => (
                                            <Badge key={a} variant="outline">{a}</Badge>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Clock className="h-5 w-5 text-brand-600" /> Upcoming</CardTitle></CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        {[
                                            { title: 'Therapy Review', sub: 'With Dr. Smith', date: 'Oct 24' },
                                            { title: 'New Assessment Due', sub: 'Mid-year check', date: 'Nov 12' },
                                        ].map((item, i) => (
                                            <div key={i} className="flex justify-between items-start text-sm border-b border-slate-100 pb-2 last:border-0">
                                                <div>
                                                    <p className="font-medium text-slate-900">{item.title}</p>
                                                    <p className="text-slate-500 text-xs mt-0.5">{item.sub}</p>
                                                </div>
                                                <span className="text-brand-600 font-medium whitespace-nowrap">{item.date}</span>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </TabsContent>

                <TabsContent value="educational">
                    <Card>
                        <CardHeader><CardTitle>Educational Details</CardTitle></CardHeader>
                        <CardContent className="grid sm:grid-cols-2 gap-4 pt-4">
                            <InfoField label="School" value={profileData.school || 'N/A'} />
                            <InfoField label="Grade" value={profileData.grade || 'N/A'} />
                            <InfoField label="Support Level" value={profileData.supportLevel || 'N/A'} />
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="medical">
                    <Card>
                        <CardHeader><CardTitle>Medical &amp; Support Details</CardTitle></CardHeader>
                        <CardContent className="grid sm:grid-cols-2 gap-4 pt-4">
                            <InfoField label="Primary Diagnosis" value={profileData.diagnosis || 'N/A'} />
                            <InfoField label="Primary Concern (Query)" value={profileData.pcMainConcern || 'Not specified'} />
                            <InfoField label="Support Level" value={profileData.supportLevel || 'N/A'} />
                            <InfoField label="Primary Contact" value={profileData.parents || 'N/A'} />
                            <InfoField label="Phone" value={profileData.contact || 'N/A'} />
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="history">
                    <Card>
                        <CardContent className="p-8 text-center text-slate-400">
                            <FileText className="h-10 w-10 mx-auto mb-3 opacity-40" />
                            <p className="text-sm">No past assessments recorded yet.</p>
                            <Button className="mt-4" size="sm" onClick={handleNewAssessment}>
                                <FileText className="mr-2 h-4 w-4" /> Start New Assessment
                            </Button>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="iep">
                    <Card>
                        <CardContent className="p-8 text-center text-slate-400">
                            <Target className="h-10 w-10 mx-auto mb-3 opacity-40" />
                            <p className="text-sm">View and manage IEP goals on the IEP Goals page.</p>
                            <Link to="/Screening_test/app/iep">
                                <Button className="mt-4" size="sm">Go to IEP Goals</Button>
                            </Link>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* Edit Profile Modal */}
            {editModalOpen && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                    <Card className="w-full max-w-lg shadow-xl border-0">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
                            <h2 className="text-lg font-bold text-slate-900">Edit Profile</h2>
                            <Button variant="ghost" size="icon" onClick={() => setEditModalOpen(false)} className="h-8 w-8 -mr-2"><X className="h-4 w-4" /></Button>
                        </div>
                        {saved ? (
                            <div className="p-12 text-center">
                                <div className="mx-auto w-16 h-16 bg-green-50 text-green-600 rounded-full flex items-center justify-center mb-4">
                                    <CheckCircle className="h-8 w-8" />
                                </div>
                                <h3 className="text-lg font-bold text-slate-900">Profile Updated!</h3>
                            </div>
                        ) : (
                            <form onSubmit={handleSaveEdit}>
                                <CardContent className="space-y-4 pt-6">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium text-slate-700 block mb-1">Full Name</label>
                                            <Input value={editForm.name || ''} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} required />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-slate-700 block mb-1">Age</label>
                                            <Input type="number" value={editForm.age || ''} onChange={e => setEditForm(f => ({ ...f, age: e.target.value }))} />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-slate-700 block mb-1">Diagnosis</label>
                                        <Input value={editForm.diagnosis || ''} onChange={e => setEditForm(f => ({ ...f, diagnosis: e.target.value }))} />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium text-slate-700 block mb-1">Support Level</label>
                                            <select className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                                                value={editForm.supportLevel || 'Level 1'} onChange={e => setEditForm(f => ({ ...f, supportLevel: e.target.value }))}>
                                                <option>Level 1</option><option>Level 2</option><option>Level 3</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-slate-700 block mb-1">Phone</label>
                                            <Input value={editForm.contact || ''} onChange={e => setEditForm(f => ({ ...f, contact: e.target.value }))} />
                                        </div>
                                    </div>
                                </CardContent>
                                <div className="p-6 pt-0 flex gap-3">
                                    <Button type="button" variant="outline" className="flex-1" onClick={() => setEditModalOpen(false)}>Cancel</Button>
                                    <Button type="submit" className="flex-1">Save Changes</Button>
                                </div>
                            </form>
                        )}
                    </Card>
                </div>
            )}
        </div>
    );
}

function InfoField({ label, value }) {
    return (
        <div>
            <p className="text-xs font-medium text-slate-500 mb-1">{label}</p>
            <p className="text-sm text-slate-900 font-medium">{value}</p>
        </div>
    );
}
